import { useId, type InputHTMLAttributes } from 'react';
import { FieldError, FieldHint, FieldLabel, type FieldChrome } from './formField';
import { controlClasses, describedBy } from './formHelpers';

/** Props for {@link Input}. Extends the native input attributes. */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>, FieldChrome {
  /** Optional stable id. Auto-generated when omitted. */
  id?: string | undefined;
}

/**
 * Labelled text input for the design system.
 *
 * Accessibility (spec 08 §7):
 * - A real `<label>` is associated via `htmlFor`/`id` (generated with `useId`).
 * - `hint` and `error` are linked through `aria-describedby`; `aria-invalid`
 *   reflects the error state.
 * - The error is conveyed by text + icon (never colour alone, spec 08 §2) and a
 *   visible `focus-visible` ring is always present in both themes.
 */
export function Input({
  label,
  id,
  error,
  hint,
  labelAddon,
  className = '',
  required,
  ...props
}: InputProps): JSX.Element {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel htmlFor={inputId} label={label} required={required} addon={labelAddon} />
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
      <input
        id={inputId}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(hintId, errorId, Boolean(hint), Boolean(error))}
        className={controlClasses(Boolean(error), className)}
        {...props}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}
